import React, { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import PanelLayoutContent from "components/layout/PanelLayoutContent"
import { useNavigate } from "react-router-dom"
import { TslNewsletterService } from "services/api-workers-service"
import type {
  TslNewsletterArticle,
  TslNewsletterArticleStatus,
  TslNewsletterSource,
} from "services/api-workers-service"
import { SentryService } from "services/SentryService"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import Select from "ui/components/atoms/select"
import Spinner from "ui/components/atoms/spinner"
import Tag from "ui/components/atoms/tag"
import { Label, P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { Pagination } from "ui/components/molecules/pagination"
import { formatDate } from "utils/date"

import {
  TSL_STATUSES,
  TSL_TAGS,
  priorityColor,
  priorityLabel,
  statusColor,
  statusLabel,
  tagLabel,
} from "./data"
import TslNewsletterNav from "./TslNewsletterNav"

const PAGE_SIZE = 15

const ArticlesList = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [status, setStatus] = useState<string>("")
  const [sourceId, setSourceId] = useState<string>("")
  const [tag, setTag] = useState<string>("")
  const [minPriority, setMinPriority] = useState<string>("")
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionInfo, setActionInfo] = useState<string | null>(null)

  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(0)
    }, 300)
  }, [search])

  const { data: sourcesData } = useQuery({
    queryKey: ["tsl", "sources", "lookup"],
    queryFn: () => TslNewsletterService.listSources({ limit: 200 }),
  })

  const sourceMap = useMemo(() => {
    const map = new Map<string, TslNewsletterSource>()
    sourcesData?.items.forEach(s => map.set(s.id, s))
    return map
  }, [sourcesData])

  const offset = page * PAGE_SIZE
  const { data, isLoading, isError } = useQuery({
    queryKey: ["tsl", "articles", { debouncedSearch, status, sourceId, tag, minPriority, offset }],
    queryFn: () =>
      TslNewsletterService.listArticles({
        q: debouncedSearch || undefined,
        status: (status as TslNewsletterArticleStatus) || undefined,
        source_id: sourceId || undefined,
        tag: tag || undefined,
        min_priority: minPriority ? Number(minPriority) : undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  })

  const reprocessMutation = useMutation({
    mutationFn: (id: string) => TslNewsletterService.reprocessArticle(id),
    onSuccess: () => {
      setActionInfo("Artykuł przetworzony na nowo.")
      queryClient.invalidateQueries({ queryKey: ["tsl", "articles"] })
    },
    onError: (e: any) => {
      if (e?.response?.status === 422) {
        setActionError("Nie udało się pobrać treści artykułu ze źródła — sprawdź URL artykułu.")
        return
      }
      SentryService.error("[ERROR TslNewsletterService.reprocessArticle]:", e)
      setActionError(e?.response?.data?.detail || "Nie udało się przetworzyć artykułu.")
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => TslNewsletterService.deleteArticle(id),
    onSuccess: () => {
      setActionInfo("Artykuł oznaczony jako usunięty.")
      queryClient.invalidateQueries({ queryKey: ["tsl", "articles"] })
    },
    onError: e => {
      SentryService.error("[ERROR TslNewsletterService.deleteArticle]:", e)
      setActionError("Nie udało się usunąć artykułu.")
    },
  })

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))

  const handleReprocess = (article: TslNewsletterArticle) => {
    if (!window.confirm("Ponownie przetworzyć przez AI? Zastąpi tagi, priority oraz streszczenia."))
      return
    setActionError(null)
    setActionInfo(null)
    reprocessMutation.mutate(article.id)
  }

  const handleDelete = (article: TslNewsletterArticle) => {
    if (
      !window.confirm("Oznaczyć artykuł jako usunięty? Zostanie pominięty w wysyłce newslettera.")
    )
      return
    setActionError(null)
    setActionInfo(null)
    deleteMutation.mutate(article.id)
  }

  const clearFilters = () => {
    setSearch("")
    setStatus("")
    setSourceId("")
    setTag("")
    setMinPriority("")
    setPage(0)
  }

  return (
    <PanelLayoutContent
      title="TSL Newsletter"
      text="Artykuły zebrane z RSS, kuracja redaktorska"
    >
      <TslNewsletterNav />

      <FiltersWrapper $marginBottom="M">
        <Input
          name="q"
          label="Szukaj"
          placeholder="Tytuł artykułu"
          value={search}
          onChange={({ value }) => setSearch(value)}
          $width="280px"
        />
        <Select
          name="status"
          label="Status"
          options={[["Wszystkie", ""], ...TSL_STATUSES]}
          value={status}
          onChange={({ value }) => {
            setPage(0)
            setStatus(value)
          }}
          $minWidth="180px"
        />
        <Select
          name="sourceId"
          label="Źródło"
          options={[["Wszystkie", ""], ...(sourcesData?.items.map(s => [s.name, s.id]) ?? [])]}
          value={sourceId}
          onChange={({ value }) => {
            setPage(0)
            setSourceId(value)
          }}
          $minWidth="200px"
        />
        <Select
          name="tag"
          label="Tag"
          options={[["Wszystkie", ""], ...TSL_TAGS]}
          value={tag}
          onChange={({ value }) => {
            setPage(0)
            setTag(value)
          }}
          $minWidth="220px"
        />
        <Select
          name="minPriority"
          label="Priorytet min."
          options={[
            ["Dowolny", ""],
            ["≥ 0 (Niska)", "0"],
            ["≥ 3 (Normalna)", "3"],
            ["≥ 6 (Wysoka)", "6"],
            ["≥ 9 (TOP)", "9"],
          ]}
          value={minPriority}
          onChange={({ value }) => {
            setPage(0)
            setMinPriority(value)
          }}
          $minWidth="180px"
        />
        <Button $type="secondary" onClick={clearFilters}>
          Wyczyść filtry
        </Button>
      </FiltersWrapper>

      {actionError && (
        <Alert $type="error" $small $marginBottom="S">
          {actionError}
        </Alert>
      )}
      {actionInfo && (
        <Alert $type="success" $small $marginBottom="S">
          {actionInfo}
        </Alert>
      )}

      {isError && (
        <Alert $type="error" $marginBottom="S">
          Nie udało się pobrać listy artykułów. Sprawdź klucz API i połączenie.
        </Alert>
      )}

      {data && (
        <P>
          System znalazł <span>{data.total}</span> artykułów
        </P>
      )}

      {isLoading ? (
        <Row $justifyContent="center" $padding="M">
          <Spinner $size="30px" />
        </Row>
      ) : !data || data.items.length === 0 ? (
        <EmptyState>
          <P $marginBottom="S">Brak artykułów dla wybranych filtrów.</P>
          <Button $type="secondary" onClick={clearFilters}>
            Wyczyść filtry
          </Button>
        </EmptyState>
      ) : (
        <ListWrapper>
          {data.items.map(article => {
            const sourceName = sourceMap.get(article.source_id)?.name ?? "—"
            const sc = statusColor(article.status)
            const pc = priorityColor(article.priority)
            return (
              <ArticleRow key={article.id}>
                <Row $alignItems="flex-start" $justifyContent="space-between">
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Row $alignItems="center" $marginBottom="XXS">
                      <Tag
                        $type="secondary"
                        $showBorder={false}
                        $background={sc.bg}
                        $color={sc.color}
                        label={statusLabel(article.status)}
                        $marginRight="XS"
                      />
                      <Tag
                        $type="secondary"
                        $showBorder={false}
                        $background={pc.bg}
                        $color={pc.color}
                        label={`P${article.priority ?? "?"} ${priorityLabel(article.priority)}`}
                        $marginRight="XS"
                      />
                      <span
                        style={{
                          textDecoration: article.status === "deleted" ? "line-through" : "none",
                          cursor: "pointer",
                        }}
                        onClick={() => navigate(article.id)}
                      >
                        <Label $marginBottom="0">{article.title || "(brak tytułu)"}</Label>
                      </span>
                    </Row>
                    <Row $alignItems="center" $marginBottom="XXS">
                      <P $small $marginBottom="0" $marginRight="S">
                        {sourceName}
                      </P>
                      {article.published_at && (
                        <P $small $marginBottom="0" $marginRight="S">
                          {formatDate(article.published_at)}
                        </P>
                      )}
                    </Row>
                    {article.tags?.length > 0 && (
                      <TagsRow>
                        {article.tags.slice(0, 3).map(t => (
                          <Tag
                            key={t}
                            $type="secondary"
                            $showBorder={false}
                            $background="background"
                            label={tagLabel(t)}
                          />
                        ))}
                        {article.tags.length > 3 && (
                          <Tag
                            $type="secondary"
                            $showBorder={false}
                            $background="background"
                            label={`+${article.tags.length - 3}`}
                          />
                        )}
                      </TagsRow>
                    )}
                  </Box>
                  <ActionsCol>
                    <Button
                      $type="secondary"
                      icon="boost"
                      onClick={() => handleReprocess(article)}
                      disabled={article.status === "deleted" || reprocessMutation.isPending}
                      loading={reprocessMutation.isPending}
                    >
                      Reprocess
                    </Button>
                    <Button
                      $type="secondary"
                      icon="trash"
                      onClick={() => handleDelete(article)}
                      disabled={article.status === "deleted"}
                    >
                      Usuń
                    </Button>
                  </ActionsCol>
                </Row>
              </ArticleRow>
            )
          })}
        </ListWrapper>
      )}

      {data && data.total > PAGE_SIZE && (
        <Pagination defaultPage={page + 1} totalPages={totalPages} onChange={e => setPage(e - 1)} />
      )}
    </PanelLayoutContent>
  )
}

export default ArticlesList

const FiltersWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  grid-gap: ${({ theme }) => theme.space.S};
`

const ListWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.XS};
`

const ArticleRow = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: white;
`

const TagsRow = styled(Row)`
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.XXS};
`

const ActionsCol = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.XS};
  margin-left: ${({ theme }) => theme.space.S};
  flex-shrink: 0;
`

const EmptyState = styled(Box)`
  padding: ${({ theme }) => theme.space.M};
  text-align: center;
  background: ${({ theme }) => theme.color.background};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  margin-bottom: ${({ theme }) => theme.space.M};
`
